import { AppHeader } from 'packages/at-shell/components/app-shell/app-header';
import { ConfigProvider, Layout, SiderProps, Spin, theme } from 'antd';
import { FC, PropsWithChildren, ReactNode, Suspense, useEffect } from 'react';
import { AppSider } from 'packages/at-shell/components/app-shell/app-sider';
import { AppToolbar } from 'packages/at-shell/components/app-shell/toolbar';
import { elementSizeAtomFam } from 'packages/lib/common-ui/state';
import { SyncAreaSize } from 'packages/lib/common-ui/sync-area-size/sync-area-size';
import {
  contentAreaId,
  globalFooterAreaId,
  layoutId,
  primaryViewAreaId,
} from 'packages/at-shell/components/app-shell/app-layout-constants';
import { useRecoilValue, useRecoilSnapshot } from 'recoil';
import { SessionTimer } from 'packages/at-shell/components/dev-mode/metrics-ui';
import { SyncAnnotationRecoilParams } from 'packages/at-shell/utils/sync-annotation-recoil-params';
import { AutosaveInFlightWarning } from 'packages/at-shell/components/app-shell/autosave-warnng';
import { SyncAppPersonaAtom } from 'packages/at-shell/utils/tool-query-params';
import { devModeAtom } from 'packages/at-shell/state/recoil/ui';
import { PillHud } from 'packages/at-shell/components/hud-pill';
import { Styles } from 'packages/at-shell/components/app-shell/styles';
import { merge } from 'lodash';
import { SyncMultiTaskWorkflowParams } from 'packages/at-shell/utils/multitask-workflow-query-params';
import { SyncMultiTaskWorkflowData } from 'packages/at-shell/utils/multitask-workflow-query-params';
import { TaskAnnotationHistoryWindow } from 'packages/at-shell/components/app-shell/task-annotation-history';
import { SyncTaskQueue } from 'packages/at-shell/utils/sync-task-queue';
import { useBodyClass } from 'packages/lib/common-ui/theme/use-body-theme';

export function DebugObserver(): null {
  const snapshot = useRecoilSnapshot();
  useEffect(() => {
    console.debug('The following atoms were modified:');
    for (const node of snapshot.getNodes_UNSTABLE({ isModified: true })) {
      console.debug(node.key, snapshot.getLoadable(node));
    }
  }, [snapshot]);

  return null;
}

const { Content, Footer } = Layout;

export interface AppShellConfig {
  /** Minimum primary view width. String must be in px or vw, i.e. "300px" or "50vw" */
  primaryViewMinWidth?: string;
  /** Optional title for the Sider, shown in the top app bar. When set to false, does not occupy any space in the app bar, and expand/collapse button is removed. */
  siderTitle?: ReactNode | false;
  /** Sider min width, as a number, in pixels */
  siderMinWidth?: number;
  /** Initial sider width when app first loads, in pixels */
  defaultSiderOpenWidth?: number;
  siderResizable?: boolean;
  defaultIsSiderOpen?: boolean;
  toolbarProps?: SiderProps;
}
export type AppShellConfigRequired = Required<AppShellConfig>;

export const defaultAppShellConfig: AppShellConfigRequired = {
  defaultSiderOpenWidth: 340,
  siderMinWidth: 48,
  siderTitle: false,
  primaryViewMinWidth: '50vw',
  siderResizable: true,
  defaultIsSiderOpen: true,
  toolbarProps: {
    width: 48,
  },
};

export interface AnnotationToolLayoutProps {
  sceneActions?: ReactNode;
  /** An optional toolbar shown on the left side of the screen */
  toolbar?: ReactNode;
  /** The main view, typically where the primary scene to annotate is shown.  */
  primaryView?: ReactNode;
  secondaryView?: ReactNode;
  globalFooter?: ReactNode;
  siderTitle?: string | ReactNode | false;
  /** Sider side-panel on the right, often used to show UI for annotations. */
  sider?: ReactNode;
  /** For footer UI elements. Typically this is the Submit button, Undo/Redo buttons, cloud sync status, etc. */
  siderFooterActions?: ReactNode;
  effects?: () => ReactNode;
  /** Optional user-configurable overrides of default values */
  config?: AppShellConfig;
  /** Dark or light theme for the app shell and its children */
  theme?: 'dark' | 'light';
  /** Set to true when the layout component is used as a loading skeleton. Used internally by the App Shell. */
  skeletonMode?: boolean;
  /** Current AppLayout syncs atoms to URL for RAIL tools as well which we don't need for all tools like Multi-task workflow tools.
   * Here we can send the custom URL sync component which will run instead if not null.*/
  isMultiTaskWorkflow?: boolean;
}

export const AppLayoutFallbackSpinner: FC<{
  colorTheme: AnnotationToolLayoutProps['theme'];
}> = ({ colorTheme }) => (
  <div
    style={{
      background: colorTheme === 'dark' ? '#000000' : undefined,
      minHeight: '100vh',
      minWidth: '100%',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
    }}
  >
    <Spin />
  </div>
);

export const AppLayout: FC<PropsWithChildren<AnnotationToolLayoutProps>> = (
  props
) => {
  const dev_mode = useRecoilValue(devModeAtom);

  const {
    sceneActions,
    secondaryView,
    primaryView,
    globalFooter,
    sider,
    siderFooterActions,
    toolbar,
    children,
    effects,
    skeletonMode,
    theme: colorTheme,
    isMultiTaskWorkflow = false,
  } = props;

  // Add theme to body class to allow global css overrides e.g. ant notifications
  useBodyClass(colorTheme);

  // Allow user to selectively override any specific default
  // eslint-disable-next-line react/destructuring-assignment
  const config = merge(defaultAppShellConfig, props.config);
  const hasSider = !!sider || !!siderFooterActions;

  return (
    <>
      <Suspense fallback={<AppLayoutFallbackSpinner colorTheme={colorTheme} />}>
        <Styles colorTheme={colorTheme} siderTitle={config.siderTitle} />
        {!skeletonMode && (
          <>
            {/* Multitask workflow tools like Classification Enrichment don't have same query params and atoms as annotation tools (like RAIL)
            So pass a check to sync different URL query params
            e.g., Check classificationToolURLParamsSyncSelector
          */}
            {isMultiTaskWorkflow ? (
              <>
                <SyncMultiTaskWorkflowParams />
                <SyncMultiTaskWorkflowData />
              </>
            ) : (
              <>
                <SyncAnnotationRecoilParams />
                <SyncAppPersonaAtom />
                <SyncTaskQueue />
              </>
            )}
            <Suspense fallback={null}>
              <AutosaveInFlightWarning />
            </Suspense>
            {effects?.()}
          </>
        )}
        <ConfigProvider
          theme={{
            algorithm:
              colorTheme === 'dark'
                ? [theme.darkAlgorithm, theme.compactAlgorithm]
                : [theme.compactAlgorithm],
          }}
        >
          <Layout className="layout-container">
            <SyncAreaSize
              id={layoutId}
              elementSizeAtomFam={elementSizeAtomFam}
            />
            <AppHeader sceneActions={sceneActions} />
            <Suspense
              fallback={
                <div
                  style={{
                    background: '#000000',
                    height: '100vh',
                    width: '100vw',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                >
                  <Spin />
                </div>
              }
            >
              <Layout hasSider style={{ height: '100%' }}>
                {toolbar && (
                  <AppToolbar
                    tools={toolbar}
                    siderProps={config.toolbarProps}
                  />
                )}
                <Content
                  style={{
                    overflowY: 'hidden',
                    overflowX: 'hidden',
                    position: 'relative',
                  }}
                >
                  <SyncAreaSize
                    id={contentAreaId}
                    elementSizeAtomFam={elementSizeAtomFam}
                  />
                  <div id={primaryViewAreaId}>
                    {primaryView}
                    <PillHud placement="topLeft" />
                  </div>
                  {children}
                  {dev_mode || (!''.length && <SessionTimer />)}
                </Content>
                {!secondaryView ? null : secondaryView}
              </Layout>
              {hasSider && (
                <AppSider config={config} footerActions={siderFooterActions}>
                  {sider}
                </AppSider>
              )}
              <Footer style={{ padding: 0, margin: 0, position: 'relative' }}>
                {globalFooter ?? null}
                <SyncAreaSize
                  id={globalFooterAreaId}
                  elementSizeAtomFam={elementSizeAtomFam}
                />
              </Footer>
            </Suspense>
          </Layout>
          <TaskAnnotationHistoryWindow />
        </ConfigProvider>
      </Suspense>
    </>
  );
};
export default AppLayout;
